import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  EmployeeCalendarAssignment,
  HolidayCalendar,
  HolidayCalendarFormInput,
} from '../types/holidayCalendar';

type Envelope<T> = { success: boolean; message: string; data: T };

function numberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function textOrNull(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function boolFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function mapEmployee(row: Record<string, unknown>) {
  const employeeId = Number(row.employeeId ?? row.employee_id);
  if (!Number.isFinite(employeeId) || employeeId <= 0) return null;
  const employeeNumber = textOrNull(row.employeeNumber ?? row.employee_number);
  const firstName = textOrNull(row.firstName ?? row.first_name);
  const lastName = textOrNull(row.lastName ?? row.last_name);
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim() || null;
  let displayName =
    textOrNull(row.displayName ?? row.display_name) || joined || null;
  if (
    displayName &&
    employeeNumber &&
    displayName.toLowerCase() === employeeNumber.toLowerCase()
  ) {
    displayName = joined;
  }
  return {
    employeeId,
    employeeNumber,
    displayName,
    firstName,
    lastName,
  };
}

function mapCalendar(row: unknown): HolidayCalendar | null {
  if (!row || typeof row !== 'object') return null;
  const item = row as Record<string, unknown>;
  const holidayCalendarId = Number(
    item.holidayCalendarId ?? item.holiday_calendar_id,
  );
  if (!Number.isFinite(holidayCalendarId) || holidayCalendarId <= 0) return null;
  const employeesRaw = Array.isArray(item.employees) ? item.employees : [];
  return {
    holidayCalendarId,
    holidayCalendarUuid: textOrNull(
      item.holidayCalendarUuid ?? item.holiday_calendar_uuid,
    ),
    organizationId: numberOrNull(item.organizationId ?? item.organization_id),
    locationId: numberOrNull(item.locationId ?? item.location_id),
    calendarCode: textOrNull(item.calendarCode ?? item.calendar_code) || '',
    calendarName: textOrNull(item.calendarName ?? item.calendar_name) || '',
    description: textOrNull(item.description),
    calendarYear: Number(item.calendarYear ?? item.calendar_year) || 0,
    isDefault: boolFlag(item.isDefault ?? item.is_default),
    isActive:
      item.isActive == null && item.is_active == null
        ? true
        : boolFlag(item.isActive ?? item.is_active),
    isDeleted: boolFlag(item.isDeleted ?? item.is_deleted),
    rowVersion: Number(item.rowVersion ?? item.row_version) || 1,
    employeeCount:
      numberOrNull(item.employeeCount ?? item.employee_count) ?? undefined,
    employees: employeesRaw
      .map(emp =>
        emp && typeof emp === 'object'
          ? mapEmployee(emp as Record<string, unknown>)
          : null,
      )
      .filter((emp): emp is NonNullable<typeof emp> => emp != null),
    createdAt: textOrNull(item.createdAt ?? item.created_at),
    updatedAt: textOrNull(item.updatedAt ?? item.updated_at),
  };
}

export async function getHolidayCalendars(options?: {
  search?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<{ items: HolidayCalendar[]; totalRecords: number }> {
  const response = await apiClient.get<
    Envelope<{
      items: unknown[];
      pagination?: { totalRecords?: number };
    }>
  >(API_ENDPOINTS.HOLIDAY_CALENDARS.LIST, {
    params: {
      search: options?.search || undefined,
      isActive: options?.isActive == null ? undefined : options.isActive ? 1 : 0,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 100,
    },
  });
  const payload = response.data.data;
  const items = (payload.items || [])
    .map(mapCalendar)
    .filter((row): row is HolidayCalendar => row != null);
  return {
    items,
    totalRecords: Number(payload.pagination?.totalRecords ?? items.length),
  };
}

export async function getHolidayCalendar(
  holidayCalendarId: number,
): Promise<HolidayCalendar> {
  const response = await apiClient.get<Envelope<unknown>>(
    API_ENDPOINTS.HOLIDAY_CALENDARS.DETAILS(holidayCalendarId),
  );
  const mapped = mapCalendar(response.data.data);
  if (!mapped) throw new Error('Holiday calendar was not found');
  return mapped;
}

export async function getEmployeeCalendarAssignments(): Promise<
  EmployeeCalendarAssignment[]
> {
  const response = await apiClient.get<Envelope<{ items?: unknown[] } | unknown[]>>(
    API_ENDPOINTS.HOLIDAY_CALENDARS.EMPLOYEE_ASSIGNMENTS,
  );
  const raw = response.data.data;
  const rows = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { items?: unknown[] }).items)
      ? (raw as { items: unknown[] }).items
      : [];
  const assignments: EmployeeCalendarAssignment[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const mapped = mapEmployee(row as Record<string, unknown>);
    if (!mapped) continue;
    const item = row as Record<string, unknown>;
    assignments.push({
      employeeId: mapped.employeeId,
      employeeNumber: mapped.employeeNumber,
      displayName: mapped.displayName,
      firstName: mapped.firstName ?? null,
      lastName: mapped.lastName ?? null,
      holidayCalendarId: numberOrNull(
        item.holidayCalendarId ?? item.holiday_calendar_id,
      ),
      calendarCode: textOrNull(item.calendarCode ?? item.calendar_code),
      calendarName: textOrNull(item.calendarName ?? item.calendar_name),
      isDefault: boolFlag(item.isDefault ?? item.is_default),
    });
  }
  return assignments;
}

export async function createHolidayCalendar(
  input: HolidayCalendarFormInput,
): Promise<HolidayCalendar> {
  const response = await apiClient.post<Envelope<unknown>>(
    API_ENDPOINTS.HOLIDAY_CALENDARS.CREATE,
    {
      calendarCode: input.calendarCode || null,
      calendarName: input.calendarName,
      description: input.description || null,
      calendarYear: input.calendarYear,
      isDefault: input.isDefault,
      employeeIds: input.employeeIds,
    },
  );
  const mapped = mapCalendar(response.data.data);
  if (!mapped) throw new Error('Holiday calendar could not be created');
  return mapped;
}

export async function updateHolidayCalendar(
  holidayCalendarId: number,
  input: HolidayCalendarFormInput,
): Promise<HolidayCalendar> {
  const response = await apiClient.put<Envelope<unknown>>(
    API_ENDPOINTS.HOLIDAY_CALENDARS.UPDATE(holidayCalendarId),
    {
      calendarName: input.calendarName,
      description: input.description || null,
      calendarYear: input.calendarYear,
      isDefault: input.isDefault,
      isActive: input.isActive ?? true,
      rowVersion: input.rowVersion ?? null,
      employeeIds: input.employeeIds,
    },
  );
  const mapped = mapCalendar(response.data.data);
  if (!mapped) throw new Error('Holiday calendar could not be updated');
  return mapped;
}

export async function deleteHolidayCalendar(
  holidayCalendarId: number,
): Promise<void> {
  await apiClient.delete(
    API_ENDPOINTS.HOLIDAY_CALENDARS.DELETE(holidayCalendarId),
  );
}
